import {
  checkIPAD,
  checkMob,
  getBrowser,
  getBrowserName,
  getBrowserVersion,
  getMobileOperatingSystem,
  getOS,
  getRegexExpFor,
} from '@/lib/commonHelpers';
// From the leaf module, NOT commonHelpers — see lsHelpers.ts (ChromeOS boot TDZ).
import { cdnBaseUrl } from '@/config/appConfig';
import { getFromLS, setToLS } from '@/lib/lsHelpers';
import { useRealtimeExperienceStore } from '@/pages/experiences/store/realtimeExperienceStore';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';

// Define a type for app types
export type AppType = 'seb' | 'mac' | 'ios' | 'cros' | 'web' | 'win';

// Low-storage threshold (bytes). When the browser/app grants less than this much
// total IndexedDB quota we treat the device as too low on storage to safely hold
// offline exam responses. 1 GiB = 1024^3 bytes. Single source of truth — used by
// the ConnectivityTest diagnostics row to decide FAIL vs PASS. Never inline this
// literal elsewhere.
export const LOW_STORAGE_QUOTA_THRESHOLD_BYTES = 1_073_741_824; // 1 GiB

export interface StorageEstimateResult {
  quota: number | null;
  usage: number | null;
  supported: boolean;
}

/**
 * Measures the device's total/used IndexedDB-eligible storage via the
 * StorageManager API (`navigator.storage.estimate()`).
 *
 * Returns `supported: false` (with null quota/usage) whenever the API is absent
 * (older Safari / SEB) or the estimate rejects/throws. Callers MUST treat
 * `supported: false` as "cannot measure" and never block the student on it — a
 * missing measurement API is not evidence of low storage (see spec § Error
 * handling). The gate decision is computed client-side from `supported && quota`.
 */
export const getStorageEstimate = async (): Promise<StorageEstimateResult> => {
  try {
    if (!navigator.storage || typeof navigator.storage.estimate !== 'function') {
      return { supported: false, quota: null, usage: null };
    }
    const estimate = await navigator.storage.estimate();
    return {
      supported: true,
      quota: typeof estimate.quota === 'number' ? estimate.quota : null,
      usage: typeof estimate.usage === 'number' ? estimate.usage : null,
    };
  } catch (error) {
    console.error('getStorageEstimate failed:', error);
    return { supported: false, quota: null, usage: null };
  }
};

/**
 * True only when the API is supported AND a concrete quota below the threshold
 * was measured. Unsupported (or unmeasured quota) → false. Used by the
 * ConnectivityTest diagnostics row to flag the storage check as FAIL.
 */
export const isStorageQuotaLow = (estimate: StorageEstimateResult): boolean =>
  estimate.supported &&
  estimate.quota !== null &&
  estimate.quota < LOW_STORAGE_QUOTA_THRESHOLD_BYTES;

/**
 * Human-readable storage size: ≥ 1 GiB renders in GB (e.g. "2 GB", "1.5 GB"),
 * anything smaller renders in MB (e.g. "512 MB"). GB is rounded to one decimal
 * with any trailing ".0" dropped so whole values read cleanly. Shared by the
 * ConnectivityTest row and the session deviceInfo write so both show the same
 * format. Returns null for a null input (unmeasured quota/usage).
 */
export const formatStorageSize = (bytes: number | null): string | null => {
  if (bytes === null) return null;
  const MIB = 1024 * 1024;
  const GIB = 1024 * MIB;
  if (bytes >= GIB) {
    return `${parseFloat((bytes / GIB).toFixed(1))} GB`;
  }
  return `${Math.round(bytes / MIB)} MB`;
};

/** 13.0.0+ ships per-architecture Mac builds (APL-10011). */
export type MacDownloadArch = 'silicon' | 'intel';

/**
 * Terminal default when NO rollout flag matches at all — the final `else`
 * branch of the flag chain (12.2.0 — dev call, 2026-07-29).
 */
const MAC_DEFAULT_VERSION = '12.2.0';

export const getOfflineAppDownloadUrl = (
  appType: string,
  region: string,
  enabledFeatures: Record<string, boolean> = {},
  returnAAC = false,
  // 13.0.0+ ships per-architecture builds. Silicon is the default — intel
  // only on an explicit intel request (dev call, 2026-07-29).
  arch?: MacDownloadArch
) => {
  let finalUrl;
  switch (appType) {
    case 'mac':
      if (region === 'china') {
        if (enabledFeatures.release_mac_app_v_13_1_0) {
          finalUrl =
            arch === 'intel'
              ? 'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v13.1.0-CN-Intel-2026-08-22.pkg'
              : 'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v13.1.0-CN-Silicon-2026-08-22.pkg';
        } else if (enabledFeatures.release_mac_app_v_13_0_0) {
          finalUrl =
            arch === 'intel'
              ? 'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v13.0.0-CN-Intel-2026-08-04.pkg'
              : 'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v13.0.0-CN-Silicon-2026-08-04.pkg';
        } else if (enabledFeatures.release_mac_app_v_12_3_1) {
          finalUrl =
            'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v12.3.1_2026_06_05.dmg';
        } else if (enabledFeatures.release_mac_app_v_12_3_0) {
          finalUrl =
            'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v12.3.0_2026_06_04.dmg';
        } else if (enabledFeatures.release_mac_app_v_12_2_1) {
          finalUrl =
            'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v12.2.1_2026_05_08.dmg';
        } else if (enabledFeatures.release_mac_app_v_12_2_0) {
          finalUrl =
            'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v12.2.0_2026_02_06.dmg';
        } else if (enabledFeatures.release_mac_app_v_12_1_0) {
          finalUrl =
            'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v12.1.0_2025_04_04.dmg';
        } else if (enabledFeatures.release_mac_app_v_11_0_0) {
          finalUrl =
            'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v11.0.0_2024_11_17.dmg';
        } else {
          finalUrl =
            'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v12.2.0_2026_02_06.dmg';
        }
      } else {
        if (enabledFeatures.release_mac_app_v_13_1_0) {
          finalUrl =
            arch === 'intel'
              ? `${cdnBaseUrl}/AP_Mac/AssessPrep_Mac_v13.1.0-GL-Intel-2026-09-01.pkg`
              : `${cdnBaseUrl}/AP_Mac/AssessPrep_Mac_v13.1.0-GL-Silicon-2026-09-01.pkg`;
        } else if (enabledFeatures.release_mac_app_v_13_0_0) {
          // `cdnBaseUrl` instead of a hardcoded host (13.x only): a
          // staging build downloads the staging upload. Older versions below
          // stay hardcoded — those objects only exist on the legacy CloudFront
          // distribution, so pointing them here today would 404 them.
          finalUrl =
            arch === 'intel'
              ? `${cdnBaseUrl}/AP_Mac/AssessPrep_Mac_v13.0.0-GL-Intel-2026-08-04.pkg`
              : `${cdnBaseUrl}/AP_Mac/AssessPrep_Mac_v13.0.0-GL-Silicon-2026-08-04.pkg`;
        } else if (enabledFeatures.release_mac_app_v_12_3_2) {
          finalUrl =
            'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep_Mac_v12.3.2_2026_06_10.dmg';
        } else if (enabledFeatures.release_mac_app_v_12_3_1) {
          finalUrl =
            'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep_Mac_v12.3.1_2026_06_05.dmg';
        } else if (enabledFeatures.release_mac_app_v_12_3_0) {
          finalUrl =
            'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep_Mac_v12.3.0_2026_06_04.dmg';
        } else if (enabledFeatures.release_mac_app_v_12_2_1) {
          finalUrl =
            'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep_Mac_v12.2.1_2026_05_08.dmg';
        } else if (enabledFeatures.release_mac_app_v_12_2_0) {
          finalUrl =
            'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep_Mac_v12.2.0_2026_02_06.dmg';
        } else if (enabledFeatures.release_mac_app_v_12_1_0) {
          finalUrl =
            'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep_Mac_v12.1.0_2025_04_04.dmg';
        } else if (enabledFeatures.release_mac_app_v_11_0_0) {
          finalUrl =
            'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep_Mac_v11.0.0_2024_11_17.dmg';
        } else {
          finalUrl =
            'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep_Mac_v12.2.0_2026_02_06.dmg';
        }
      }
      break;
    case 'win8_1':
      if (region === 'china') {
        finalUrl =
          'https://production-eks-assets.assessprep.cn/SEB_setup/SEB_3.5.0.544_SetupBundle.exe';
      } else {
        finalUrl = 'https://tinyurl.com/2s4krphv'; // 3.5.0
      }
      break;
    case 'win10':
      if (region === 'china') {
        finalUrl =
          'https://production-eks-assets.assessprep.cn/SEB_setup/AP_SEB_3.10.2.9999_x64.exe';
      } else {
        finalUrl =
          'https://v2res1.assessprep.com/manual_uploads/SEB%20Win/AP_SEB_3.10.2.9999_x64.exe';
      }
      break;
    case 'ios':
      finalUrl = 'https://apps.apple.com/us/app/id1543293245';
      break;
    default:
      finalUrl = '';
      break;
  }
  return finalUrl;
};

/**
 * Download URL for the Download-apps surfaces' hrefs. Thin wrapper over the
 * static flag ladder (APL-10011 — the manifest-driven resolution is parked
 * with APL-10453); null when the ladder has no URL for the appType.
 */
export const useOfflineAppDownloadUrl = (
  appType: string,
  region: string,
  enabledFeatures: Record<string, boolean> = {},
  arch?: MacDownloadArch
): string | null => getOfflineAppDownloadUrl(appType, region, enabledFeatures, false, arch) || null;

/**
 * Mac "Supported versions" copy tiers: 12.3.0+ → macOS 14.4 (PKCE + anti-cheat
 * work require Sequoia point-release). Orgs pinned to an 11.x/12.1 flag still
 * download those macOS-11-capable builds, so they keep the macOS-11 copy.
 * Terminal (flagless) tier is macOS 12: the flagless org runs the 12.2.0
 * default (dev call, 2026-07-29). Shared by MacTab + OfflineApp.
 */
export const getMacSupportedVersionsKey = (enabledFeatures: Record<string, boolean>): string =>
  enabledFeatures.release_mac_app_v_13_1_0 ||
  enabledFeatures.release_mac_app_v_12_3_0 ||
  enabledFeatures.release_mac_app_v_12_3_1 ||
  enabledFeatures.release_mac_app_v_12_3_2 ||
  enabledFeatures.release_mac_app_v_13_0_0
    ? 'OfflineApp.supported_versions_macos_14_4_and_above'
    : enabledFeatures.release_mac_app_v_12_2_0
    ? 'OfflineApp.supported_versions_macos_12_and_above'
    : enabledFeatures.release_mac_app_v_11_0_0 || enabledFeatures.release_mac_app_v_12_1_0
    ? 'OfflineApp.supported_versions_macos_11_and_above'
    : 'OfflineApp.supported_versions_macos_12_and_above';

/**
 * Legacy Mac builds a single student may be granted through an accommodation.
 *
 * The two split on AAC mode at macOS 10.15.4, the version Apple introduced
 * Automatic Assessment Configuration: 12.1.0 uses AAC so it needs 10.15.4 or
 * above; 9.0.2 predates AAC, so it is the only build for a Mac BELOW 10.15.4
 * and gives that Mac no AAC lockdown.
 *
 * 12.2.0 is deliberately absent (dev call, APL-12328) — it is the ladder's
 * terminal default. A `legacy_ap_mac_12_2_0_allowed` grant written while it
 * was an option is inert: nothing reads that key.
 *
 * Newest first — render order.
 */
export const LEGACY_MAC_BUILDS = [
  {
    version: '12.1.0',
    accommodationKey: 'legacy_ap_mac_12_1_0_allowed',
    supportedVersionsKey: 'OfflineApp.supported_versions_macos_10_15_4_and_above_with_aac',
    labelKey: 'AccommodationModal.ap_mac_v12_1_0',
    testId: 'download-legacy-mac-1210-link',
    switchTestId: 'accommodation-legacy-app-1210',
    url: {
      china:
        'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v12.1.0_2025_04_04.dmg',
      global: 'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep_Mac_v12.1.0_2025_04_04.dmg',
    },
  },
  {
    version: '9.0.2',
    accommodationKey: 'legacy_ap_mac_9_0_2_allowed',
    supportedVersionsKey: 'OfflineApp.supported_versions_macos_below_10_15_4_without_aac',
    labelKey: 'AccommodationModal.ap_mac_v9_0_2',
    // Both ids predate the versioned naming — kept, Playwright locators use them.
    testId: 'download-legacy-mac-link',
    switchTestId: 'accommodation-legacy-app',
    url: {
      china:
        'https://production-eks-assets.assessprep.cn/AP_Mac/AssessPrep_Mac_v9.0.2_2023_04_15.dmg',
      // Percent-encoded on the global CDN — that is the stored object key.
      global:
        'https://d15evq3hcqea3u.cloudfront.net/AP+Mac/AssessPrep%2BMac%2Bv9.0.2_2023_04_15.dmg',
    },
  },
] as const;

export type LegacyMacBuild = (typeof LEGACY_MAC_BUILDS)[number];

/** The slice of `custom_fields.accommodations` these surfaces read. */
export type LegacyMacGrants = Partial<Record<LegacyMacBuild['accommodationKey'], boolean>>;

/**
 * Download URL for a legacy build. Separate from the release ladder on
 * purpose: the ladder is keyed on org feature flags, and a legacy build is a
 * per-STUDENT grant. Routing it through `enabledFeatures` would mean a
 * server-side flag that happened to share the alias name could redirect an
 * org's whole Mac download to a retired installer.
 */
export const getLegacyMacDownloadUrl = (build: LegacyMacBuild, region: string): string =>
  region === 'china' ? build.url.china : build.url.global;

export const MAC_AUTO_UPDATE_FEED_BASE_URL = 'https://manual-uploads.assessprep.com/AP+Mac'; // IMP: This url is not working right now , Need to updated when allow auto update feature for mac app https://d15evq3hcqea3u.cloudfront.net/AP+Mac/appcast_assessprep_mac_app_12_3_0_update_staging.xml
const appVersionsList = {
  seb: [
    { version: '3.10.2.9999', status: 'latest', isCustom: true },
    { version: '3.10.1.9999', status: 'old', isCustom: true }, // Release for all with windows 10 support
    { version: '3.10.0.9999', status: 'old', isCustom: true },
    { version: '3.8.0.9999', status: 'old', isCustom: true }, // only keep 9999 versions as latest or old - rest unsupported
    { version: '3.10.0', status: 'unsupported' },
    { version: '3.9.0', status: 'unsupported' },
    { version: '3.8.0.999', status: 'unsupported', isCustom: true },
    { version: '3.8.0', status: 'unsupported' },
    { version: '3.5.0', status: 'unsupported' },
    { version: '3.4.1', status: 'unsupported' },
    { version: '3.4.0', status: 'unsupported' },
    { version: '3.3.2', status: 'unsupported' },
    { version: '3.3.1', status: 'unsupported' },
    { version: '3.3.0', status: 'unsupported' },
    { version: '3.2.0', status: 'unsupported' },
    { version: '3.1.1', status: 'unsupported' },
    { version: '3.1.0', status: 'unsupported' },
    { version: '3.0.1', status: 'unsupported' },
    { version: '3.0.0', status: 'unsupported' },
    { version: '2.4.1', status: 'unsupported' },
  ],
  mac: [
    {
      version: '13.1.0',
      status: 'latest',
      feedUrl: `${MAC_AUTO_UPDATE_FEED_BASE_URL}/appcast_assessprep_mac_app_13_1_0_update.xml`,
      versionFlag: 'release_mac_app_v_13_1_0',
    },
    {
      version: '13.0.0',
      status: 'latest',
      feedUrl: `${MAC_AUTO_UPDATE_FEED_BASE_URL}/appcast_assessprep_mac_app_13_0_0_update.xml`,
      versionFlag: 'release_mac_app_v_13_0_0',
    },
    {
      version: '12.3.2',
      status: 'old',
      feedUrl: `${MAC_AUTO_UPDATE_FEED_BASE_URL}/appcast_assessprep_mac_app_12_3_2_update.xml`,
      versionFlag: 'release_mac_app_v_12_3_2',
    },
    {
      version: '12.3.1',
      status: 'old',
      feedUrl: `${MAC_AUTO_UPDATE_FEED_BASE_URL}/appcast_assessprep_mac_app_12_3_1_update.xml`,
      versionFlag: 'release_mac_app_v_12_3_1',
    },
    {
      version: '12.3.0',
      status: 'old',
      feedUrl: `${MAC_AUTO_UPDATE_FEED_BASE_URL}/appcast_assessprep_mac_app_12_3_0_update.xml`,
      versionFlag: 'release_mac_app_v_12_3_0',
    },
    {
      version: '12.2.1',
      status: 'old',
      feedUrl: `${MAC_AUTO_UPDATE_FEED_BASE_URL}/appcast_assessprep_mac_app_12_2_1_update.xml`,
      versionFlag: 'release_mac_app_v_12_2_1',
    },
    {
      version: '12.2.0',
      status: 'latest',
      feedUrl: `${MAC_AUTO_UPDATE_FEED_BASE_URL}/appcast_assessprep_mac_app_12_2_0_update.xml`,
      versionFlag: 'release_mac_app_v_12_2_0',
    },
    {
      version: '12.1.0',
      status: 'unsupported',
      feedUrl: null,
      versionFlag: 'release_mac_app_v_12_1_0',
    },
    {
      version: '12.0.0',
      status: 'unsupported',
      feedUrl: null,
      versionFlag: 'release_mac_app_v_12_0_0',
    },
    {
      version: '11.0.0',
      status: 'unsupported',
      feedUrl: null,
      versionFlag: 'release_mac_app_v_11_0_0',
    },
    {
      version: '10.2.0',
      status: 'unsupported',
      feedUrl: null,
      versionFlag: 'release_mac_app_v_10_2_0',
    },
    { version: '10.1.0', status: 'unsupported' }, //AAC mode app
    { version: '10.0.0', status: 'unsupported' }, //IMP: v10.0.0 released for only few schools
    { version: '9.0.2', status: 'unsupported' }, //Major running app (without ACC mode) before release v10.1.0
    { version: '9.0.1', status: 'unsupported' },
    { version: '9.0.0', status: 'unsupported' },
    { version: '8.0.1', status: 'unsupported' },
    { version: '8.0.0', status: 'unsupported' },
    { version: '7.0.1', status: 'unsupported' },
    { version: '7.0.0', status: 'unsupported' },
  ],
  ios: [
    { version: '6.0.0', status: 'latest' },
    { version: '5.7.0', status: 'old' },
    { version: '5.6.0', status: 'old' },
    { version: '5.5.0', status: 'old' },
    { version: '5.3.0', status: 'old' },
    { version: '5.0.0', status: 'old' },
    { version: '4.1.0', status: 'old' },
    { version: '3.2.0', status: 'old' },
    { version: '2.0.5', status: 'old' },
    { version: '1.0.2', status: 'old' },
    { version: '1.0.1', status: 'unsupported' },
    { version: '1.0.0', status: 'unsupported' },
  ],
  cros: [{ version: '107', status: 'latest' }],
  web: {
    chrome: [{ version: 84, status: 'unsupported' }],
    firefox: [{ version: 81, status: 'unsupported' }],
    safari: [{ version: 14, status: 'unsupported' }],
  },
  win: [],
};

export const customSEBVersions = appVersionsList.seb
  .filter((versionDetail) => versionDetail.isCustom)
  .map((versionDetail) => versionDetail.version);

export const getLatestAppVersion = (appType: AppType, enabledFeatures = {}, returnAAC = false) => {
  // Use optional chaining and handle the case where the array might be empty
  const versions = appVersionsList[appType];
  let latestVersion = null;

  if (Array.isArray(versions) && versions.length > 0) {
    latestVersion = versions[0].version;
  }

  return latestVersion;
};

export const isNewAppVersion = (appVersion: string | null | undefined, compareVersion: string) => {
  // desired output: appVersion >= compareVersion ? true : false
  if (!appVersion || !compareVersion) {
    return false;
  }

  const appVersionComponents = appVersion.split('.');
  const compareVersionComponents = compareVersion.split('.');

  // Compare the components in turn
  for (let i = 0; i < appVersionComponents.length; i++) {
    const appVersionComponent = parseInt(appVersionComponents[i] ?? '0'); // default to '0' if there are no more components
    const compareVersionComponent = parseInt(compareVersionComponents[i] ?? '0'); // default to '0' if there are no more components

    if (appVersionComponent > compareVersionComponent) {
      return true;
    } else if (appVersionComponent < compareVersionComponent) {
      return false;
    }
  }

  // If all components are equal, the versions are the same
  return true;
};

// Native user_response reads/writes (APL-11641) ship in the Mac app at 13.0.0
// and the iOS app at 6.0.0. These thresholds gate whether native_user_responses
// can exist for a given attempt.
export const MAC_NATIVE_MIN = '13.0.0';
export const IOS_NATIVE_MIN = '6.0.0';

// Did the STUDENT take a given test on a native app new enough to write
// native_user_responses? Reads the attempt's stored device info — NOT the
// current session's app (see getAppType). Gates the "Native Backup" source in
// the grading backup dropdown, its merged-view native fetch, and the write-side
// hydration hook.
export const studentUsedNativeApp = (deviceInfo: any): boolean => {
  if (!deviceInfo) return false;
  if (deviceInfo.app_type === 'mac') return isNewAppVersion(deviceInfo.appVersion, MAC_NATIVE_MIN);
  if (deviceInfo.app_type === 'ios') return isNewAppVersion(deviceInfo.appVersion, IOS_NATIVE_MIN);
  return false;
};

export const getAppNativeVars = (appType: AppType) => {
  const userAgent: any = navigator.userAgent || window.navigator.userAgent;
  let appNativeVars: any = {};
  if (appType === 'seb') {
    appNativeVars.appVersion =
      userAgent.match(/SEB\/([^\s]+)/g) &&
      userAgent.match(/SEB\/([^\s]+)/g)[0].replace(/SEB\//g, '');
  }
  if (appType === 'mac' || appType === 'ios') {
    const localAppNativeVars = localStorage.getItem('apNativeVars');
    if (
      localAppNativeVars &&
      localAppNativeVars !== undefined &&
      localAppNativeVars !== 'undefined'
    ) {
      appNativeVars = JSON.parse(localAppNativeVars);
    }
  }
  return appNativeVars;
};

export const getAppVersion = (appType: AppType) => {
  const userAgent = navigator.userAgent || window.navigator.userAgent;
  let appVersion = '';
  if (appType === 'seb') {
    const match = userAgent.match(/SEB\/([^\s]+)/g);
    appVersion = match ? match[0].replace(/SEB\//g, '') : '';
  }
  if (appType === 'mac' || appType === 'ios') {
    const appNativeVars = getAppNativeVars(appType);
    appVersion = appNativeVars.appVersion;
  }
  return appVersion;
};

/**
 * Region-aware display version, e.g. "13.0.0 (gl)". Returns the native-provided
 * `appVersionWithRegion` string when present (Global/China builds from v12.4.0);
 * otherwise falls back to the bare app version (older builds inject neither key).
 *
 * DISPLAY / TELEMETRY ONLY (APL-11625) — never feed this into isNewAppVersion or
 * any semver comparison; the "(gl)" suffix is not a version component.
 */
export const getAppVersionWithRegion = (appType: AppType): string => {
  const appNativeVars = getAppNativeVars(appType);
  if (appNativeVars?.appVersionWithRegion) return appNativeVars.appVersionWithRegion;
  // `?? ''` so web(where getAppNativeVars returns {}) and any other
  // missing-appVersion case render '' rather than the literal 'undefined' in
  // ungated consumers like ConnectivityTest (APL-11625 review fix).
  return appNativeVars.appVersion ?? '';
};

/**
 * Single source of truth for the reported app-name + version string used in
 * realtime device-info logs (e.g. "AssessPrep Mac App 13.0.0 (gl)"). The version
 * segment is region-aware via getAppVersionWithRegion (APL-11625), so a gl vs cn
 * build is distinguishable in the reported string. Shared by StartTestButton and
 * InitRealtimeExperience so the string is built in exactly one place.
 *
 * DISPLAY / TELEMETRY ONLY — not for version comparison.
 */
export const getAppVersionString = (appType: AppType): string => {
  const versionWithRegion = getAppVersionWithRegion(appType);
  if (appType === 'mac') return `AssessPrep Mac App ${versionWithRegion}`;
  if (appType === 'ios') return `AssessPrep iOS App ${versionWithRegion}`;
  if (appType === 'cros') return 'Chromebook';
  return `SEB ${versionWithRegion}`;
};

const isVersionSupported = (appType: AppType, currentVersion: string, requiredVersion: string) => {
  return (
    (appType === 'mac' && isNewAppVersion(currentVersion, requiredVersion)) ||
    (appType === 'ios' && isNewAppVersion(currentVersion, requiredVersion))
  );
};

/**
 * Single source of truth for "is this native bridge method available on the
 * student's current app build?". Each native method was introduced in a
 * specific app version; calling it on an older build means the native side
 * never registered a handler, so the executePromise channel never resolves
 * and the call rejects after its timeout — surfacing as Sentry noise. Gate
 * every version-sensitive bridge call through here so we only invoke a method
 * once the running build actually ships it. The minimum version per method is
 * documented inline on each branch below (and in OfflineAppService's
 * methodMappings).
 */
export const checkNativeMethodSupported = (appType: AppType, methodName: string) => {
  let methodSupported = false;
  const currentAppVersion = getAppVersion(appType);

  if (methodName === 'getSecurityStatus' || methodName === 'showQuitAlert') {
    // Security status + quit alert: mac from 10.2.0, ios from 3.2.0
    if (appType === 'mac' && isNewAppVersion(currentAppVersion, '10.2.0')) {
      methodSupported = true;
    }

    if (appType === 'ios' && isNewAppVersion(currentAppVersion, '3.2.0')) {
      methodSupported = true;
    }
  } else if (methodName === 'openChildWindow' || methodName === 'openNewWindow') {
    // New-tab / child-window support landed in the mac app at 10.1.0
    if (appType === 'mac' && isNewAppVersion(currentAppVersion, '10.1.0')) {
      methodSupported = true;
    }
  } else if (methodName === 'openContentInModal') {
    // In-app modal content viewer added in the mac app at 11.0.0
    if (appType === 'mac' && isNewAppVersion(currentAppVersion, '11.0.0')) {
      methodSupported = true;
    }
  } else if (methodName === 'exitApp') {
    // Native exit-app handler available on ios from 3.2.0
    if (appType === 'ios' && isNewAppVersion(currentAppVersion, '3.2.0')) {
      methodSupported = true;
    }
  } else if (methodName === 'printLog') {
    // Native log forwarding added in the mac app at 12.1.1
    if (appType === 'mac' && isNewAppVersion(currentAppVersion, '12.1.1')) {
      methodSupported = true;
    }
  } else if (
    methodName === 'getNativeRunningProcessApps' ||
    methodName === 'getSystemTextReplacements' ||
    methodName === 'signWithSecureEnclave'
  ) {
    // New mac-only bridge methods shipped in 12.3.0. Older builds don't
    // register these handlers, so the bridge call would time out and report
    // to Sentry — gate the calling points on this check (APL-11633).
    if (appType === 'mac' && isNewAppVersion(currentAppVersion, '12.3.0')) {
      methodSupported = true;
    }
  } else if (methodName === 'signInNativeFirestore') {
    // Native-owned user_response writes (APL-11641): React hands the Firestore
    // custom token to native, which signs into Firebase Auth and takes over the
    // user_response batch mutations. Ships in the Mac app at 13.0.0 and the iOS
    // app at 6.0.0 — older builds never register the handler, so the bridge call
    // would time out into Sentry.
    if (appType === 'mac' && isNewAppVersion(currentAppVersion, '13.0.0')) {
      methodSupported = true;
    }
    if (appType === 'ios' && isNewAppVersion(currentAppVersion, '6.0.0')) {
      methodSupported = true;
    }
  } else if (methodName === 'nativeBatchMutation') {
    // Native-owned user_response batch writes (APL-11641): once native is
    // signed into Firebase Auth (signInNativeFirestore), React builds the same
    // operations it would have committed over the web SDK and hands them across
    // the bridge fire-and-forget for native to write directly. Ships in the Mac
    // app at 13.0.0 and the iOS app at 6.0.0 alongside signInNativeFirestore — older
    // builds never register the handler, and web/seb/win have no native bridge
    // (→ fall through to the web batchMutation).
    if (appType === 'mac' && isNewAppVersion(currentAppVersion, '13.0.0')) {
      methodSupported = true;
    }
    if (appType === 'ios' && isNewAppVersion(currentAppVersion, '6.0.0')) {
      methodSupported = true;
    }
  } else if (methodName === 'getFirestorePendingWritesStatus') {
    // Native pending-writes status (APL-11641): the mount waits on native's
    // write queue flushing since the web SDK can't see it. Mac 13.0.0 / iOS
    // 6.0.0, alongside signInNativeFirestore / nativeBatchMutation.
    if (appType === 'mac' && isNewAppVersion(currentAppVersion, '13.0.0')) {
      methodSupported = true;
    }
    if (appType === 'ios' && isNewAppVersion(currentAppVersion, '6.0.0')) {
      methodSupported = true;
    }
  }

  return methodSupported;
};

export const checkIfShouldDispatchChangeSecurity = (appType: AppType, experienceSettings: any) => {
  const currentAppVersion = getAppVersion(appType);

  // !isNewAppVersion(currentAppVersion, "4.0.0") checks if the app version is lower than 4.0.0.
  // In the iPad (iOS) app version 4.0.0 and above, session end is allowed only when triggered by the session end button.
  // Note: The native session end button appears on all screens as part of the bottom navigation bar.
  // Therefore, we want to session termination (changeSecurity = false) triggered via the session end button only.
  return (
    experienceSettings.mode === 'offline' &&
    ((appType === 'mac' && !isNewAppVersion(currentAppVersion, '12.0.0')) ||
      (appType === 'ios' && !isNewAppVersion(currentAppVersion, '4.0.0')))
  );
};

export const getOSVersion = (appType: AppType, systemInfo: string, finalBrowserVersion = '') => {
  let finalVersion = '';
  // @nitin why Mac OS also here? can remove?
  if (appType === 'mac' || appType === 'ios') {
    if (systemInfo) {
      const matches = systemInfo.match(/Version \d.+ \(B/gi);
      const version = matches?.[0];
      if (version) {
        finalVersion = version.replace(/Version | \(B/gi, '');
      }
    }
  }
  console.log('getOSVersion appType', appType);
  if (appType === 'cros') {
    // In case of ChromeOS, OS version = Chrome browser version
    finalVersion = finalBrowserVersion;
  }
  return finalVersion;
};

export const getDeviceInfo = (appType: AppType) => {
  console.log('calling getDeviceInfo');
  const finalBrowserName = getBrowserName();
  let browserVersion;
  if (getBrowser().includes('Safari')) {
    browserVersion = getBrowserVersion(getBrowser(), /\/\d+([^\s]+)/g, null, 0);
  } else {
    browserVersion = getBrowserVersion(getBrowser(), /\/\d+/g, null, 0);
  }

  const finalBrowserVersion = browserVersion ? browserVersion.replace(/\//g, '') : '';
  console.log('inside get browser==>', finalBrowserVersion);

  const appNativeVars = getAppNativeVars(appType);
  const osVersion = getOSVersion(appType, appNativeVars.systemInfo, finalBrowserVersion);
  const appVersion = getAppVersion(appType);
  const deviceInfo = {
    os: checkMob() || checkIPAD() ? getMobileOperatingSystem() : getOS(),
    browser: finalBrowserName + '-' + finalBrowserVersion,
    osVersion: osVersion,
    appVersion: appVersion,
    app_type: appType,
    ...appNativeVars,
  };
  return deviceInfo;
};

// Picks the most informative device info to persist on submission:
// the latest native-app entry from the student's used devices (since app version
// reporting cares about the app, not the browser); falls back to the most recent
// entry overall (browser) when the student never used a native app.
export const pickSubmissionDeviceInfo = (usedDevices: any[] = []) => {
  for (let i = usedDevices.length - 1; i >= 0; i--) {
    const entry = usedDevices[i];
    if (entry?.app_type && entry.app_type !== 'web') {
      return entry;
    }
  }
  return usedDevices[usedDevices.length - 1];
};

// Lockdown app_types — when the original submission came from one of these we
// expect the student to re-open the assessment in the desktop / iPad app
// before syncing local storage. `seb` runs on Windows (Safe Exam Browser);
// `mac` / `ios` are the AssessPrep native apps; `win` is the Windows native
// download helper; `cros` is the Chromebook lockdown experience.
const LOCKDOWN_APP_TYPES: AppType[] = ['seb', 'mac', 'ios', 'win', 'cros'];

export const isLockdownAppType = (appType: AppType | string | undefined): boolean => {
  if (!appType) return false;
  return LOCKDOWN_APP_TYPES.includes(appType as AppType);
};

export const getAppType = () => {
  let appType: AppType = 'web'; // Default value

  let queryParams;
  if (location) {
    queryParams = queryString.parse(location.search);
  }

  const userAgent = window.navigator.userAgent;
  if (userAgent && userAgent.indexOf('AssessPrepSEB_HDC5hjdqmjypb3qxn') > -1) {
    appType = 'seb';
  }
  if ((window as WindowExtended).downloadHelper) {
    appType = 'win';
  } else if (userAgent && userAgent.indexOf('AssessPrepMac') > -1) {
    // To do with custom Suffix in userAgent like SEB. current condition is setting appType to mac for ipad also
    appType = 'mac';
  } else if (userAgent && userAgent.indexOf('AssessPrepIOS') > -1) {
    // To do with custom Suffix in userAgent like SEB. current condition is setting appType to mac for ipad also
    appType = 'ios';
  } else if (
    userAgent &&
    userAgent.indexOf('CrOS') > -1 &&
    ((queryParams && queryParams.appType === 'cros') || getFromLS('appType') == 'cros')
  ) {
    // CrOS for Chromebooks
    setToLS('appType', 'cros');
    appType = 'cros';
  }

  return appType;
};

export type AppMethodParams = {
  key: string;
  value: any;
};

export interface DownloadHelper {
  quitAssessPrepWebView?: (args: any) => void;
  changeSecurity?: (args: any) => void;
  nativeLog?: (args: any) => void;
  [key: string]: ((args: any) => void) | undefined;
}

export interface WindowExtended {
  appType?: AppType;
  downloadHelper?: DownloadHelper;
  webkit?: {
    messageHandlers?: {
      macDownloadHelper?: {
        postMessage: (args: any) => void;
      };
      iosDownloadHelper?: {
        postMessage: (args: any) => void;
      };
    };
  };
  executePromise?: (guid: string, data: any) => void;
  executeSetFocusLost?: (value: any) => void;
  executeSetSecurityWasInterrupted?: (value: any) => void;
  executeSetLogOnSecurityResume?: (value: any) => void;
  executeOnBeforeEndSession?: (value: any) => void;
  executeSetLogOnQuit?: (value: any) => void;
  executeSetAutoUpdateStatus?: (value: any) => void;
  // remove `close` since it's already defined in Window
}

export const getWhiteListUrls = (experienceSettings?: any): string => {
  const defaultWhitelistedUrlsRegex =
    '^https://((assessprep.(com|cn))|(.*?.assessprep.(com|cn))|(.*?.managebac.(com|cn))|(.*?.faria.(org|us))|(.*?.us.faria.(org))|(.*?.fariaedu.(com))|(.*?.vimeo.com)|(vimeo.com)|(.*?.jit.si)|((login|account).(microsoftonline|live).com)|(.*?.zdusercontent.com)|(.*?.zoom.*?)|(meet.(google).(com|cn))|(apps.(google).(com|cn))|(accounts.(google).(co(m|(.([a-z]{2}))))))(/.*)?$';

  const { lockdown_config: lockdownConfig = {} } = experienceSettings ?? {};
  const { whitelist_urls_regex: customWhitelistRegex } = lockdownConfig ?? {};

  return customWhitelistRegex ?? defaultWhitelistedUrlsRegex;
};

export const macAppsToClose = [
  'com.wiheads.paste',
  'com.wiheads.paste-setapp',
  'com.sublimetext.3',
  'com.microsoft.Word',
  'com.microsoft.Powerpoint',
  'com.microsoft.Excel',
  'com.microsoft.Outlook',
  'com.apple.mail',
  'com.fiplab.clipboard', //free copy clip app
  'com.fiplab.copyclip2', //version of 2 copy clip paid app
  'net.maxbor.CopyLess', //free copy clip app
  'com.goodsnooze.MenuGPT', //mac gpt app
  'com.apple.shortcuts', // default shortcut app , its not work/app close now
  'com.apple.shortcuts.remote.help', // shortcut app , its not work/app close now
  'com.kittenyang.ios.ChatGPT-Mac-MenuBar',
  'com.vincelwt.chatgptmac',
  'com.Ai-writer.gpt3',
  'com.app.menubarx',
  'me.tanmay.AnyGPT',
  'com.smileonmymac.textexpander', //text expanders app, apple default text replacement didn't work
  'com.oslash.macapp',
  'com.bartelsmedia.PhraseExpressOSX',
  'com.federicoterzi.espanso',
  'com.typeit4me.TypeIt4MeMenu',
  'com.trankynam.aText',
  'com.macility.typinator2',
  'com.typedesk.app',
  'com.stairways.keyboardmaestro.editor',
  'com.runningwithcrayons.Alfred',
  'com.openai.chat',
];

export const getAppVersionInfo = (
  appType: AppType,
  options: { key: string; value: string; browserName?: string }
) => {
  const { key, value, browserName } = options;
  const data = appVersionsList as any;
  if (!value || !appType) {
    return {};
  }
  if (appType === 'web') {
    if (!browserName || !data[appType][browserName]) {
      return {};
    }
    return data[appType][browserName].find((item: any) => item[key] === value);
  }
  return data[appType] ? data[appType].find((item: any) => item[key] === value) : {};
};

/**
 * Per-org enforcement of the Mac 12.3.1 minimum version (APL-11285).
 *
 * Returns true iff:
 *   - the user is on the Mac app, AND
 *   - the org has opted into the policy via `enforce_mac_app_v_12_3_1_and_above`, AND
 *   - the 12.3.1 release flag is also on (so users actually have an in-app
 *     upgrade path — `getOfflineAppDownloadUrl` and `getMacAutoUpdateFeedInfo`
 *     both gate the 12.3.1 download / Sparkle feed on this flag), AND
 *   - the current Mac app version is below 12.3.1.
 *
 * Used by `checkIfUnsupportedApp` (hard block) and `checkUserAppVersionStatus`
 * (banner / monitor classification) so all surfaces tell the same story for
 * the same student in the same org.
 *
 * Operator note: when bumping the minimum version (e.g. to 13.0.0), introduce
 * a sibling helper with the new version + flag name; do not generalize this
 * one — the literal `'12.3.1'` is intentional for greppability per the
 * one-flag-per-policy convention.
 */
const isMacBelowEnforcedMinVersion = (
  appType: AppType,
  appVersion: string | null | undefined,
  enabledFeatures: any
): boolean =>
  appType === 'mac' &&
  !!enabledFeatures?.enforce_mac_app_v_12_3_0_and_above &&
  !!enabledFeatures?.release_mac_app_v_12_3_0 &&
  !isNewAppVersion(appVersion ?? '', '12.3.0');

// Mac 12.3.1+ re-signs in via native PKCE, so a webview logout cannot end the session (APL-12429).
// A build that reports no version keeps a working logout.
export const shouldDisableLogout = (appType: AppType): boolean => {
  if (appType !== 'mac') return false;
  return isNewAppVersion(getAppVersion(appType), '12.3.1');
};

export const browserUnsupported = (appType: AppType, deviceInfo: any) => {
  if (appType !== 'web') return false;

  const browserName = getBrowserName()?.toLowerCase();
  const browserVersion = getBrowserVersion(deviceInfo.browser, /\d.+/g, null, 0);
  const browserData = appVersionsList.web?.[browserName as keyof typeof appVersionsList.web]?.[0];

  return (
    browserData?.status === 'unsupported' &&
    browserVersion &&
    Number(browserVersion) <= browserData.version
  );
};

export const checkIfUnsupportedApp = (appType: AppType, enabledFeatures: any, currentUser: any) => {
  // Check if unsupported app flag is enabled in features
  const isDisableUnsupportedApps = enabledFeatures.disable_unsupported_apps; // is released for all schools
  const deviceInfo = getDeviceInfo(appType);

  // Get version status for current app/browser
  const appVersionInfo = getAppVersionInfo(appType, {
    key: 'version',
    value: deviceInfo.appVersion,
  });

  // Unsupporting SEB version if NOT in the list so students cannot use any versions that are NOT downloaded from AssessPrep
  const isUnlistedSEBVersion =
    appType === 'seb' && isEmpty(appVersionInfo) && enabledFeatures.disable_unlisted_seb_versions;

  const unsupportSEBNativeVersions =
    appType === 'seb' &&
    ['3.5.0', '3.8.0', '3.9.0', '3.10.0'].includes(deviceInfo.appVersion) &&
    enabledFeatures.disable_native_seb_versions; // Targets specific versions; released for only few schools for now.

  const hasUnsupportedVersion =
    appVersionInfo?.status === 'unsupported' ||
    isMacBelowEnforcedMinVersion(appType, deviceInfo.appVersion, enabledFeatures) ||
    isUnlistedSEBVersion ||
    unsupportSEBNativeVersions ||
    browserUnsupported(appType, deviceInfo);

  // A granted legacy accommodation lets the student RUN that exact build, not
  // just download it — without this the exam gate still blocks them.
  // `=== true` deliberately: a serializer that stores the string "false" must
  // not open an exam-lockdown bypass.
  const legacyAccommodationKey =
    appType === 'mac'
      ? LEGACY_MAC_BUILDS.find((build) => build.version === appVersionInfo?.version)
          ?.accommodationKey
      : undefined;
  const hasLegacyAccommodation =
    !!legacyAccommodationKey &&
    currentUser?.custom_fields?.accommodations?.[legacyAccommodationKey] === true;

  // App is unsupported if:
  // 1. Unsupported app flag is enabled AND
  // 2. Either app version is unsupported OR unlisted SEB version OR browser is unsupported AND
  // 3. Not running a legacy Mac build the student has an accommodation for
  return isDisableUnsupportedApps && hasUnsupportedVersion && !hasLegacyAccommodation;
};

export const checkUserAppVersionStatus = (
  appType: AppType,
  deviceInfo: { browser?: string; appVersion?: string },
  enabledFeatures: Record<string, boolean> = {}
): Record<string, any> => {
  let appVersionStatus: Record<string, any> = {};

  if (!deviceInfo) {
    return appVersionStatus;
  }

  if (appType === 'web' || appType === 'cros') {
    if (!deviceInfo.browser) {
      return appVersionStatus;
    }

    const browserVersion = getBrowserVersion(deviceInfo.browser, /\d.+/g, null, 0);
    const browserName = getBrowserName(deviceInfo.browser)?.toLowerCase();

    if (!browserName) {
      return appVersionStatus;
    }

    appVersionStatus = getAppVersionInfo(appType, {
      key: 'version',
      value: browserVersion ?? '',
      browserName,
    });
  } else {
    const getLatestVersionOfApp = getLatestAppVersion(appType, enabledFeatures);
    appVersionStatus = getAppVersionInfo(appType, {
      key: 'version',
      value: deviceInfo.appVersion ?? '',
    });

    if (appType === 'mac' && getLatestVersionOfApp === deviceInfo.appVersion) {
      appVersionStatus = { ...appVersionStatus, status: 'latest' };
    }
  }

  if (isMacBelowEnforcedMinVersion(appType, deviceInfo.appVersion, enabledFeatures)) {
    return { ...appVersionStatus, status: 'unsupported' };
  }

  return appVersionStatus;
};

export const isTabFeatureSupported = (appType: AppType) => {
  let allowNewtab = false;
  const currentSystemAppVersion = getAppVersion(appType);
  const deviceInfo = getDeviceInfo(appType);
  const deviceOsVersion = deviceInfo.osVersion;

  if (appType === 'ios') {
    allowNewtab = false;
  } else if (
    appType === 'mac' &&
    isNewAppVersion(currentSystemAppVersion, '10.1.0') &&
    isNewAppVersion(deviceOsVersion, '11.0.0')
  ) {
    //We are allowing the new tab feature from 10.1.0 for mac app
    allowNewtab = true;
  } else if (appType === 'seb' && isNewAppVersion(currentSystemAppVersion, '3.3.2')) {
    //We are allowing the new tab feature from 3.3.2 for seb app
    allowNewtab = true;
  }

  return allowNewtab;
};

export const isVideoUrlAllowedInsideApp = (url: string, appType: AppType) => {
  const isNewTabSupported = appType === 'web' ? true : isTabFeatureSupported(appType);
  let isVideoUrlAllowed = false;
  const videoPlatformUrlsRegExp = {
    ios: getRegexExpFor('zoom'),
    mac: getRegexExpFor('zoom_or_meet'),
    seb: getRegexExpFor('zoom'),
    // "seb": getRegexExpFor("zoom_or_meet"),
    // "cros": "" // not supporting for now
  };
  const videoPlatformUrlsRegExpForAppType = new RegExp(
    videoPlatformUrlsRegExp[appType as keyof typeof videoPlatformUrlsRegExp]
  );
  if (videoPlatformUrlsRegExpForAppType) {
    isVideoUrlAllowed = videoPlatformUrlsRegExpForAppType.test(url);
  }
  return isNewTabSupported && isVideoUrlAllowed;
};

export const checkIfSEBHasDynamicConfig = (appType: AppType) => {
  let hasDynamicConfig = false;
  if (appType === 'seb') {
    const userAgent = window.navigator.userAgent;
    if (userAgent && userAgent.indexOf('Dynamic_AssessPrepSEB_HDC5hjdqmjypb3qxn') > -1) {
      hasDynamicConfig = true;
    }
  }
  return hasDynamicConfig;
};

const macAppReleaseFlags = [
  'release_mac_app_v_13_1_0',
  'release_mac_app_v_13_0_0',
  'release_mac_app_v_12_3_2',
  'release_mac_app_v_12_3_1',
  'release_mac_app_v_12_3_0',
  'release_mac_app_v_12_2_1',
  'release_mac_app_v_12_2_0',
  'release_mac_app_v_12_1_0',
  'release_mac_app_v_12_0_0',
  'release_mac_app_v_11_0_0',
];

export const getMacAutoUpdateFeedInfo = (appType: AppType, enabledFeatures: any) => {
  // Return null if not a Mac app type
  if (appType !== 'mac') return null;

  // Get list of Mac app versions, return null if none found
  const appVersions = appVersionsList[appType];
  if (!appVersions) return null;

  // We want to auto update to the latest app version which is enabled in the feature flags
  // IMP: Find first enabled feature flag (sorted in descending order in macAppReleaseFlags array) that has a matching app version
  // enabledFeatures contains flags that are turned on
  // allowedAppVersionFlag checks if flag matches an app version

  let appVersionInfo = null;
  // Example:
  // macAppReleaseFlags = ['mac_app_v12_1_0', 'mac_app_v12_0_0']
  // enabledFeatures = {mac_app_v12_1_0: true, mac_app_v12_0_0: true}
  // First iteration: flag='mac_app_v12_1_0' -> appVersionInfo={version: '12.1.0', feedUrl: '...'}
  // Returns appVersionInfo for v12.1.0 since it's the first enabled flag
  for (const flag of macAppReleaseFlags) {
    const appVersionInfoForFlag = getAppVersionInfo(appType, { key: 'versionFlag', value: flag });
    if (enabledFeatures[flag] && appVersionInfoForFlag) {
      appVersionInfo = appVersionInfoForFlag;
      break; // Stop iterating once we find a match
    }
  }

  console.log('auto updatappVersionInfo====>>499', appVersionInfo);

  if (!appVersionInfo) return null;

  // Return staging feed URL if in staging mode, otherwise production feed URL
  // UnComment this line before merging to prod to make common staging feed URL
  // return import.meta.env.VITE_MODE === "staging" ? `${MAC_AUTO_UPDATE_FEED_BASE_URL}/appcast_assessprep_mac_app_update_stage.xml` : appVersionInfo.feedUrl;
  return appVersionInfo;
};

export const checkAutoUpdateSupported = (appType: AppType, enabledFeatures: any) => {
  let autoUpdateSupported = false;
  const deviceInfo = getDeviceInfo(appType);
  const currentAppVersion = getAppVersion(appType);
  const deviceOsVersion = deviceInfo.osVersion;
  //const latestAppVersion = getLatestAppVersion(appType) // TODO: remove after QA

  const macAutoUpdateFeedInfo = getMacAutoUpdateFeedInfo(appType, enabledFeatures);
  console.log('autoUpdateSupported======>>>460', macAutoUpdateFeedInfo);
  if (!macAutoUpdateFeedInfo) return false;
  const enabledAppVersion = macAutoUpdateFeedInfo.version;

  if (appType === 'mac') {
    if (
      //Auto App supported from v12.1.0
      //Output is currentAppVeresion >= 12.1.0 && not the latest version
      isNewAppVersion(currentAppVersion, '12.1.0') &&
      !isNewAppVersion(currentAppVersion, enabledAppVersion) &&
      isNewAppVersion(deviceOsVersion, '10.15.4')
    ) {
      autoUpdateSupported = true;
    }
  }
  console.log('autoUpdateSupported======>>>', autoUpdateSupported);
  return autoUpdateSupported;
};

export const getAutoUpdateType = (triggerType: string, appType: AppType, enabledFeatures: any) => {
  // Only handle Mac app type
  if (appType !== 'mac') {
    return 'normal';
  }

  // For automatic updates triggered on mount
  if (triggerType === 'on_mount') {
    // Return "force" if force updates enabled, otherwise "normal"
    return enabledFeatures.allow_mac_app_force_update ? 'force' : 'normal';
  }

  // For manual updates triggered by user click
  if (triggerType === 'on_click') {
    return 'manual';
  }

  // Default to normal update type
  return 'normal';
};

export const getAutoUpdateValues = (
  triggerType: string,
  appType: AppType,
  enabledFeatures: any
) => {
  const macAutoUpdateFeedInfo = getMacAutoUpdateFeedInfo(appType, enabledFeatures);
  if (!macAutoUpdateFeedInfo) return null;

  const currentAppVersion = getAppVersion(appType);

  const updateType = getAutoUpdateType(triggerType, appType, enabledFeatures);
  const feedUrl = macAutoUpdateFeedInfo.feedUrl;
  // import.meta.env.VITE_MODE === "staging"
  //   ? `${MAC_AUTO_UPDATE_FEED_BASE_URL}/appcast_assessprep_mac_app_update_stage.xml`
  //   : macAutoUpdateFeedInfo.feedUrl;
  return isNewAppVersion(currentAppVersion, '12.1.0')
    ? {
        updateType: updateType,
        feedUrl: feedUrl,
      }
    : updateType;
};

export function quitRequestedSuccess(isQuitRequested: boolean) {
  const { setQuitRequested } = useRealtimeExperienceStore.getState();
  setQuitRequested(isQuitRequested);
  setToLS('quitRequested', isQuitRequested.toString());
}

export const checkMobileLikeDevice = () => {
  const isMobile = checkMob();
  const isTab = checkIPAD();
  const appType = getAppType();
  return (isMobile || isTab) && appType !== 'ios';
};

/**
 * Checks if the current app version is considered old
 * @param appType - The type of application (mac, ios, seb, etc.)
 * @returns boolean
 */
export const checkIfOldApp = (appType: AppType): boolean => {
  try {
    const deviceInfo = getDeviceInfo(appType);

    // Early return if app version is not available. Web app version is not available in deviceInfo.
    if (!deviceInfo?.appVersion) {
      return false;
    }

    // Get version status for current app/browser
    const appVersionInfo = getAppVersionInfo(appType, {
      key: 'version',
      value: deviceInfo.appVersion,
    });

    return appVersionInfo?.status === 'old';
  } catch (error) {
    console.error('Error checking if old app:', error);
    return false;
  }
};

export const getMacAppVersion = (enabledFeatures: any): string => {
  const versionMap = [
    { flag: enabledFeatures.release_mac_app_v_13_1_0, version: '13.1.0' },
    { flag: enabledFeatures.release_mac_app_v_13_0_0, version: '13.0.0' },
    { flag: enabledFeatures.release_mac_app_v_12_3_2, version: '12.3.2' },
    { flag: enabledFeatures.release_mac_app_v_12_3_1, version: '12.3.1' },
    { flag: enabledFeatures.release_mac_app_v_12_3_0, version: '12.3.0' },
    { flag: enabledFeatures.release_mac_app_v_12_2_1, version: '12.2.1' },
    { flag: enabledFeatures.release_mac_app_v_12_2_0, version: '12.2.0' },
    { flag: enabledFeatures.release_mac_app_v_12_1_0, version: '12.1.0' },
    { flag: enabledFeatures.release_mac_app_v_11_0_0, version: '11.0.0' },
  ];
  return versionMap.find(({ flag }) => flag)?.version ?? MAC_DEFAULT_VERSION;
};
